import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { webRtcIceServerUrl } from '../../webrtcConfig.ts';
import {
  domError,
  isRecord,
  result,
  validation,
  validationError,
  withTimeout,
} from './shared.ts';
import { parseIceCandidate } from './webrtc.ts';

function statsRecord(report: RTCStats): RawData {
  return Object.fromEntries(Object.entries(report)) as RawData;
}

function candidateStats(report: RTCStats | undefined): RawData | null {
  if (!report) {
    return null;
  }
  const data = statsRecord(report);
  return {
    candidateType: data.candidateType ?? null,
    protocol: data.protocol ?? null,
    relayProtocol: data.relayProtocol ?? null,
    tcpType: data.tcpType ?? null,
    address: data.address ?? data.ip ?? null,
    port: data.port ?? null,
  };
}

async function selectedCandidatePair(
  connection: RTCPeerConnection,
): Promise<RawData | null> {
  const stats = await connection.getStats();
  let pair: RTCStats | null = null;
  stats.forEach((report) => {
    if (
      report.type == 'candidate-pair' &&
      report.state == 'succeeded' &&
      (report.nominated == true || pair == null)
    ) {
      pair = report;
    }
  });
  if (!pair) {
    return null;
  }
  const pairData = statsRecord(pair);
  const localId = pairData.localCandidateId;
  const remoteId = pairData.remoteCandidateId;
  const local = typeof localId == 'string' ? stats.get(localId) : undefined;
  const remote = typeof remoteId == 'string' ? stats.get(remoteId) : undefined;
  return {
    state: pairData.state ?? null,
    nominated: pairData.nominated ?? null,
    localCandidate: candidateStats(local),
    remoteCandidate: candidateStats(remote),
  };
}

async function waitForIceGathering(
  connection: RTCPeerConnection,
  candidateTypes: ReadonlySet<string>,
): Promise<void> {
  const hasPublicCandidate = (): boolean =>
    candidateTypes.has('srflx') || candidateTypes.has('relay');
  if (connection.iceGatheringState == 'complete' || hasPublicCandidate()) {
    return;
  }
  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, 8000);
    const finishWhenUsable = (): void => {
      if (connection.iceGatheringState == 'complete' || hasPublicCandidate()) {
        window.clearTimeout(timeout);
        resolve();
      }
    };
    connection.addEventListener('icecandidate', finishWhenUsable);
    connection.addEventListener('icegatheringstatechange', finishWhenUsable);
  });
}

async function requestAnswer(
  offer: RTCSessionDescriptionInit,
): Promise<RTCSessionDescriptionInit> {
  const response = await withTimeout(
    fetch('/webrtc-echo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(offer),
    }),
    20_000,
    'WebRTC signaling timed out',
  );
  const body: unknown = await response.json();
  if (
    !response.ok ||
    !isRecord(body) ||
    body.type != 'answer' ||
    typeof body.sdp != 'string'
  ) {
    throw new Error(`WebRTC signaling failed with HTTP ${response.status}`);
  }
  return { type: 'answer', sdp: body.sdp };
}

async function connectToServer(): Promise<RawData> {
  const connection = new RTCPeerConnection({
    iceServers: [{ urls: webRtcIceServerUrl }],
  });
  const candidateTypes = new Set<string>();
  connection.addEventListener('icecandidate', (event) => {
    if (!event.candidate) {
      return;
    }
    const parsed = parseIceCandidate(event.candidate.candidate);
    if (parsed) {
      candidateTypes.add(parsed.type);
    }
  });
  const channel = connection.createDataChannel('abc-test-remote');
  let offerCreated = false;
  let answerCreated = false;
  let received: string | null = null;
  let errorName: string | null = null;
  let errorMessage: string | null = null;
  try {
    try {
      const offer = await connection.createOffer();
      offerCreated = true;
      await connection.setLocalDescription(offer);
      await waitForIceGathering(connection, candidateTypes);
      if (!connection.localDescription) {
        throw new Error('WebRTC offer local description is missing');
      }
      const answer = await requestAnswer(connection.localDescription);
      answerCreated = true;
      await connection.setRemoteDescription(answer);
      await withTimeout(
        new Promise<void>((resolve) => {
          if (channel.readyState == 'open') {
            resolve();
          } else {
            channel.addEventListener('open', () => resolve(), { once: true });
          }
        }),
        10_000,
        'WebRTC remote DataChannel timed out',
      );
      const echo = new Promise<string>((resolve) => {
        channel.addEventListener(
          'message',
          (event) => resolve(String(event.data)),
          { once: true },
        );
      });
      channel.send('abc-test-ping');
      received = await withTimeout(echo, 5000, 'WebRTC remote echo timed out');
    } catch (error) {
      const detail = domError(error);
      errorName = detail.name;
      errorMessage = detail.message;
    }
    const selected = await selectedCandidatePair(connection).catch(() => null);
    return {
      offerCreated,
      answerCreated,
      iceGatheringState: connection.iceGatheringState,
      connectionState: connection.connectionState,
      dataChannel: { received },
      candidateTypes: [...candidateTypes].sort(),
      selectedCandidatePair: selected,
      errorName,
      errorMessage,
    };
  } finally {
    channel.close();
    connection.close();
  }
}

async function collect(): Promise<RawData> {
  try {
    return await connectToServer();
  } catch (error) {
    const detail = domError(error);
    return {
      errorName: detail.name,
      errorMessage: detail.message,
    };
  }
}

function format(rawData: RawData): string {
  const connected =
    isRecord(rawData.dataChannel) &&
    rawData.dataChannel.received == 'abc-test-ping';
  return connected ? 'connected' : 'connection failed';
}

async function run(): Promise<TestResult> {
  return result(await collect(), format);
}

function hasChromeSwitch(context: ValidationContext, name: string): boolean {
  const switchName = `--${name}`;
  return (
    context.hostParams?.chromeArgs.some(
      (argument) =>
        argument == switchName || argument.startsWith(`${switchName}=`),
    ) == true
  );
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const reasons: string[] = [];
  const connected =
    isRecord(rawData.dataChannel) &&
    rawData.dataChannel.received == 'abc-test-ping';
  const udpIp = context.hostParams?.ips.udpIp;
  const tcpIp = context.hostParams?.ips.tcpIp;
  const explicitlyDisabled = hasChromeSwitch(context, 'disable-web-rtc');
  const fakeWebRtcIp = hasChromeSwitch(context, 'fake-web-rtc-ip');
  const automaticallyDisabled =
    !fakeWebRtcIp && udpIp != null && udpIp != '' && udpIp != tcpIp;
  if (explicitlyDisabled || fakeWebRtcIp || automaticallyDisabled) {
    if (connected) {
      reasons.push(
        explicitlyDisabled
          ? 'WebRTC completed the remote DataChannel echo test with --disable-web-rtc'
          : fakeWebRtcIp
            ? 'WebRTC completed the remote DataChannel echo test with --fake-web-rtc-ip'
            : 'WebRTC completed the remote DataChannel echo test although UDP IP differs from TCP IP',
      );
    } else {
      return {
        valid: true,
        reason: null,
        note: explicitlyDisabled
          ? 'WebRTC remote connection failure is expected because --disable-web-rtc is present'
          : fakeWebRtcIp
            ? 'WebRTC remote connection failure is expected because --fake-web-rtc-ip is present'
            : 'WebRTC remote connection failure is expected because UDP IP differs from TCP IP',
      };
    }
  } else if (!connected && udpIp == '') {
    return {
      valid: true,
      reason: null,
      note: 'WebRTC remote connection failure is expected because UDP IP is empty',
    };
  } else if (udpIp != '') {
    if (rawData.offerCreated != true || rawData.answerCreated != true) {
      reasons.push('WebRTC offer and server answer were not created');
    }
    if (!connected) {
      reasons.push('WebRTC did not complete the remote DataChannel echo test');
    }
  }
  return validation(reasons);
}

export const webRtcRemoteFunctionalityTest: TestDefinition = {
  id: 'webrtcRemoteFunctionality',
  title: 'WebRTC remote functionality',
  category: 'variableProfile',
  format,
  run,
  validate,
};
